// src/pages/Settings/Integrations.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceStore } from '@/stores/workspace';
import { BackButton } from '@/components/BackButton';
import {
  usePricesApiStatus,
  useSavePricesApiKey,
  useTestPricesApiKey,
  useDeletePricesApiKey,
} from '@/lib/api-integrations';

export default function IntegrationsPage() {
  const { t } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);
  const status = usePricesApiStatus(ws?.id ?? null);
  const save = useSavePricesApiKey();
  const test = useTestPricesApiKey();
  const remove = useDeletePricesApiKey();

  const [keyInput, setKeyInput] = useState('');

  const onSave = async () => {
    if (!keyInput) return;
    await save.mutateAsync(keyInput);
    setKeyInput('');
  };

  const lastTestLabel = (() => {
    if (!status.data?.last_test_at) return t('integrations.notTested');
    const dt = new Date(status.data.last_test_at * 1000);
    return `${dt.toLocaleString()} — ${status.data.last_test_ok ? '✓' : '✗'}`;
  })();

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4">
      <div className="flex items-start gap-3">
        <BackButton to="/settings" />
        <div>
          <h1 className="text-xl font-semibold">{t('integrations.pageTitle')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('integrations.pageSubtitle')}</p>
        </div>
      </div>

      <div className="rounded-xl border bg-card p-4 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">PricesAPI</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {t('integrations.pricesapi.description')}{' '}
              <a
                href="https://pricesapi.io"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                pricesapi.io
              </a>
            </p>
          </div>
          {status.data?.configured ? (
            <span className="rounded bg-green-900/40 text-green-300 px-2 py-0.5 text-xs">
              {t('integrations.statusActive')}
            </span>
          ) : (
            <span className="rounded bg-zinc-700 text-zinc-300 px-2 py-0.5 text-xs">
              {t('integrations.statusNotConfigured')}
            </span>
          )}
        </div>

        {!status.data?.configured && (
          <div className="space-y-2">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="pricesapi_..."
              className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
            />
            <button
              onClick={onSave}
              disabled={!keyInput || save.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {save.isPending ? t('common.saving') : t('integrations.saveKey')}
            </button>
          </div>
        )}

        {status.data?.configured && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              {t('integrations.lastTest')}: {lastTestLabel}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => test.mutate()}
                disabled={test.isPending}
                className="rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
              >
                {test.isPending ? t('integrations.testing') : t('integrations.testConnection')}
              </button>
              <button
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
                className="rounded-md border border-red-900/40 text-red-400 px-3 py-2 text-sm hover:bg-red-900/20 disabled:opacity-50"
              >
                {t('integrations.removeKey')}
              </button>
            </div>
            {test.data && !test.data.ok && (
              <p className="text-xs text-red-400">
                {t('integrations.testFailed')}: {test.data.detail}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
